import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { Query, Mutation } from '../api'

const PROJECT_ROUTE = /^\/project\/([^/]+)/

/**
 * Keeps the active project in step with the URL.
 *
 * Module pages read their project from the server-side `me.currentProject`, not
 * from the `:projectId` route param, so opening a shared link used to render the
 * recipient's own project under someone else's URL — no error, just quietly the
 * wrong data. Switching the project is what the sidebar selector already does;
 * this does the same when the URL asks for a different one.
 *
 * Returns `syncing` so the layout can hold rendering until the switch lands —
 * otherwise the page mounts against the old project and fires a round of queries
 * for it first.
 */
export const useProjectRouteSync = (): { syncing: boolean } => {
  const location = useLocation()
  const { data } = useQuery(Query.ME)
  const [updateUser, { loading }] = useMutation(Mutation.UPDATE_USER, {
    refetchQueries: [{ query: Query.ME }],
    awaitRefetchQueries: true,
  })

  // A failed switch must not become a retry storm: the effect's inputs are
  // unchanged by the failure, so without this it would fire again immediately,
  // forever. One attempt per project id.
  const attempted = useRef<Set<string>>(new Set())

  const routeProjectId = location.pathname.match(PROJECT_ROUTE)?.[1]
  const currentProjectId = data?.me?.currentProject?.id
  const projects: Array<{ id: string }> = data?.projects || []

  // Only switch to a project the user actually has. An unknown id is left alone
  // rather than silently landing them somewhere else.
  const needsSwitch = Boolean(
    routeProjectId &&
      currentProjectId &&
      routeProjectId !== currentProjectId &&
      projects.some((p) => p.id === routeProjectId),
  )

  useEffect(() => {
    if (!needsSwitch || !routeProjectId || attempted.current.has(routeProjectId)) return
    attempted.current.add(routeProjectId)
    updateUser({ variables: { projectId: routeProjectId } }).catch(() => {
      // Swallowed on purpose: the page still renders, just against the previous
      // project. Surfacing a toast here would fire on every route change while
      // the network is down.
    })
  }, [needsSwitch, routeProjectId, updateUser])

  useEffect(() => {
    // Once a switch has landed, allow a later navigation back to that project to
    // switch again (the user may have moved away in between).
    if (routeProjectId && routeProjectId === currentProjectId) {
      attempted.current.delete(routeProjectId)
    }
  }, [routeProjectId, currentProjectId])

  return { syncing: needsSwitch || loading }
}

export default useProjectRouteSync
